/**
 * =============================================================================
 * TemplateModuleSelector — seleção de módulos e itens (mestre x detalhe)
 *
 * Coluna esquerda: módulos, com marcação e contagem de itens.
 * Coluna direita: coleções e itens do módulo aberto, com busca e contadores.
 *
 * Formato devolvido em `TemplateSelection`:
 *
 *   { crm: { products: ['Rastreador'], pipelines: null } }
 *
 *   - coleção com `null`  -> leva a coleção inteira
 *   - coleção com lista   -> leva apenas os itens listados
 *   - coleção ausente     -> não é levada
 *
 * Componente puro de apresentação: quem usa informa os itens de cada módulo
 * (carregados sob demanda) e recebe as mudanças por callback.
 *
 * @version 2.1.0
 * RELEASE: 2026-09-08 v2.1.0 - Módulo ainda sem resposta mostra indicador de
 *          carregamento no lugar da quantidade (evita zero enganoso).
 * RELEASE: 2026-09-08 v2.0.1 - Restringe o menu lateral à rolagem vertical
 *          e mantém a quantidade fixa e sempre visível à direita.
 * RELEASE: 2026-09-08 v2.0.0 - Leiaute mestre x detalhe, busca por item,
 *          contadores por coleção e grade responsiva.
 * RELEASE: 2026-09-08 v1.0.0 - Versão inicial (refatoração da tela).
 * =============================================================================
 */
import React, { useMemo, useState } from 'react';
import { Loader2, Search } from 'lucide-react';

import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import type { TemplateCollectionItems, TemplateSelection } from '@/lib/template-system/api';

/** Rótulos amigáveis das coleções mais comuns. */
const COLECAO_LABEL: Record<string, string> = {
  workspaces: 'Workspaces',
  pipelines: 'Funis',
  phases: 'Fases',
  products: 'Produtos',
  product_categories: 'Categorias de produto',
  price_tables: 'Tabelas de preço',
  price_table_items: 'Itens de tabela de preço',
  lead_sources: 'Origens',
  loss_reasons: 'Motivos de perda',
  playbooks: 'Playbooks',
  playbook_steps: 'Etapas de playbook',
  playbook_bindings: 'Vínculos de playbook',
  templates: 'Modelos',
  categories: 'Categorias',
  classifications: 'Classificações',
  completion_reasons: 'Motivos de conclusão',
  checklist_templates: 'Modelos de checklist',
  departments: 'Departamentos',
  labels: 'Etiquetas',
  queues: 'Filas',
  groups: 'Grupos',
  flows: 'Fluxos',
  definitions: 'Definições',
  versions: 'Versões',
  rules: 'Regras',
  dashboards: 'Painéis',
  programs: 'Programas',
  campaigns: 'Campanhas',
  materials: 'Materiais',
  profiles: 'Perfis',
  menu_items: 'Itens de menu',
  event_types: 'Tipos de evento',
  document_types: 'Tipos de documento',
  proposal_types: 'Tipos de proposta',
  deal_tags: 'Etiquetas de negócio',
  vehicle_categories: 'Categorias de veículo',
};

/** Módulo apresentado na lista. */
export interface TemplateSelectorModule {
  key: string;
  name: string;
  description: string;
}

interface TemplateModuleSelectorProps {
  /** Módulos exibidos, na ordem correta de aplicação. */
  modules: TemplateSelectorModule[];
  /** Módulos marcados. */
  selectedModules: string[];
  /** Alterna um módulo inteiro. */
  onToggleModule: (key: string) => void;
  /** Seleção granular atual. */
  selection: TemplateSelection;
  /** Atualiza a seleção granular. */
  onSelectionChange: (selection: TemplateSelection) => void;
  /** Itens já carregados por módulo. */
  itemsByModule: Record<string, TemplateCollectionItems[]>;
  /** Módulos com carregamento em andamento. */
  loadingModules?: string[];
  /** Avisa que o módulo foi aberto (para carregar os itens sob demanda). */
  onExpandModule?: (key: string) => void;
  /** Módulo aberto na coluna de detalhe (controlado por quem usa). */
  activeModule?: string | null;
  /** Avisa a troca do módulo aberto. */
  onActiveModuleChange?: (key: string) => void;
}

export function TemplateModuleSelector({
  modules,
  selectedModules,
  onToggleModule,
  selection,
  onSelectionChange,
  itemsByModule,
  loadingModules = [],
  onExpandModule,
  activeModule,
  onActiveModuleChange,
}: TemplateModuleSelectorProps) {
  // Módulo aberto: controlado por fora quando informado, senão local
  const [ativoLocal, setAtivoLocal] = useState<string | null>(modules[0]?.key ?? null);
  const [busca, setBusca] = useState('');

  const ativo = activeModule !== undefined ? activeModule : ativoLocal;

  /** Abre um módulo na coluna de detalhe. */
  const abrirModulo = (key: string) => {
    if (activeModule === undefined) setAtivoLocal(key);
    onActiveModuleChange?.(key);
    onExpandModule?.(key);
    setBusca('');
  };

  /**
   * Itens marcados de uma coleção (por padrão, todos).
   * A seleção pode conter valores que não são lista (ex.: mapas de configuração);
   * nesses casos consideramos a coleção inteira.
   */
  const marcados = (moduleKey: string, colecao: string, todos: string[]): string[] => {
    const doModulo = selection[moduleKey];
    if (!doModulo || doModulo[colecao] === undefined) return todos;
    const valor = doModulo[colecao];
    return Array.isArray(valor) ? valor : todos;
  };

  /** Marca/desmarca um item de uma coleção. */
  const alternarItem = (moduleKey: string, colecao: string, rotulo: string, todos: string[]) => {
    const doModulo = { ...(selection[moduleKey] ?? {}) };
    // `null` significa "coleção inteira": ao desmarcar um item, materializa a lista
    const atuais = marcados(moduleKey, colecao, todos);
    const proximos = atuais.includes(rotulo)
      ? atuais.filter((r) => r !== rotulo)
      : [...atuais, rotulo];

    doModulo[colecao] = proximos.length === todos.length ? null : proximos;
    onSelectionChange({ ...selection, [moduleKey]: doModulo });
  };

  /** Marca/desmarca a coleção inteira. */
  const alternarColecao = (moduleKey: string, colecao: string, todos: string[]) => {
    const doModulo = { ...(selection[moduleKey] ?? {}) };
    const atuais = marcados(moduleKey, colecao, todos);
    doModulo[colecao] = atuais.length === 0 ? null : [];
    onSelectionChange({ ...selection, [moduleKey]: doModulo });
  };

  /** Marca ou limpa todas as coleções do módulo aberto. */
  const definirTudoDoModulo = (moduleKey: string, tudo: boolean) => {
    const colecoes = itemsByModule[moduleKey] ?? [];
    const doModulo: Record<string, string[] | null> = {};
    for (const colecao of colecoes) {
      doModulo[colecao.collection] = tudo ? null : [];
    }
    onSelectionChange({ ...selection, [moduleKey]: doModulo });
  };

  /** Total de itens escolhidos de um módulo. */
  const totalEscolhidos = (moduleKey: string) => {
    const colecoes = itemsByModule[moduleKey] ?? [];
    return colecoes.reduce(
      (soma, c) => soma + marcados(moduleKey, c.collection, c.labels).length,
      0,
    );
  };

  const colecoesAtivas = ativo ? itemsByModule[ativo] ?? [] : [];
  const carregandoAtivo = !!ativo && loadingModules.includes(ativo);
  const moduloAtivo = modules.find((m) => m.key === ativo) ?? null;

  /** Coleções filtradas pela busca (mantém as que têm itens correspondentes). */
  const colecoesFiltradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return colecoesAtivas;
    return colecoesAtivas
      .map((c) => ({
        ...c,
        visiveis: c.labels.filter((l) => l.toLowerCase().includes(termo)),
      }))
      .filter((c) => c.visiveis.length > 0);
  }, [colecoesAtivas, busca]);

  return (
    <div className="grid h-full min-h-0 gap-3 lg:grid-cols-[280px_minmax(0,1fr)]">
      {/* ----------------------------- Coluna mestre ----------------------------- */}
      <div className="min-h-0 rounded-md border">
        <ScrollArea
          axis="y"
          reserveScrollbarSpace
          className="h-full max-h-[52vh] lg:max-h-none"
        >
          <div className="p-1.5 space-y-1">
            {modules.map((modulo) => {
              const marcado = selectedModules.includes(modulo.key);
              const total = (itemsByModule[modulo.key] ?? []).reduce(
                (soma, c) => soma + c.labels.length,
                0,
              );

              return (
                <button
                  type="button"
                  key={modulo.key}
                  onClick={() => abrirModulo(modulo.key)}
                  className={cn(
                    'flex min-w-0 w-full items-start gap-2 overflow-hidden rounded-md px-2 py-2 text-left transition-colors',
                    ativo === modulo.key ? 'bg-accent' : 'hover:bg-muted/60',
                  )}
                >
                  <Checkbox
                    className="mt-0.5"
                    checked={marcado}
                    onCheckedChange={() => onToggleModule(modulo.key)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={modulo.name}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{modulo.name}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {modulo.description}
                    </span>
                  </span>
                  {/* Enquanto os itens não chegam, mostra carregando (nunca zero) */}
                  {loadingModules.includes(modulo.key) ? (
                    <Loader2 className="mt-1 h-3 w-3 shrink-0 animate-spin text-muted-foreground" />
                  ) : (
                    total > 0 && (
                      <Badge
                        variant="secondary"
                        className="mt-0.5 min-w-8 shrink-0 justify-center text-[10px]"
                      >
                        {marcado ? totalEscolhidos(modulo.key) : total}
                      </Badge>
                    )
                  )}
                </button>
              );
            })}
          </div>
        </ScrollArea>
      </div>

      {/* ---------------------------- Coluna de detalhe --------------------------- */}
      <div className="flex min-h-0 flex-col rounded-md border">
        <div className="flex flex-wrap items-center gap-2 border-b px-3 py-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">
              {moduloAtivo?.name ?? 'Escolha um módulo'}
            </p>
            {moduloAtivo && (
              <p className="truncate text-xs text-muted-foreground">{moduloAtivo.description}</p>
            )}
          </div>

          {colecoesAtivas.length > 0 && ativo && (
            <>
              <div className="relative w-full sm:w-56">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Buscar item"
                  className="h-8 pl-7 text-xs"
                />
              </div>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 text-xs"
                onClick={() => definirTudoDoModulo(ativo, true)}
              >
                Tudo
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 text-xs"
                onClick={() => definirTudoDoModulo(ativo, false)}
              >
                Nada
              </Button>
            </>
          )}
        </div>

        <ScrollArea className="min-h-0 flex-1">
          <div className="space-y-4 p-3">
            {carregandoAtivo && (
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Carregando itens…
              </p>
            )}

            {!carregandoAtivo && !ativo && (
              <p className="text-xs text-muted-foreground">
                Selecione um módulo à esquerda para ver o que ele leva.
              </p>
            )}

            {!carregandoAtivo && ativo && colecoesAtivas.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Este módulo não tem cadastros nesta conta.
              </p>
            )}

            {!carregandoAtivo &&
              colecoesFiltradas.map((colecao) => {
                const visiveis =
                  'visiveis' in colecao
                    ? (colecao as TemplateCollectionItems & { visiveis: string[] }).visiveis
                    : colecao.labels;
                const escolhidos = marcados(ativo as string, colecao.collection, colecao.labels);
                const todosMarcados = escolhidos.length === colecao.labels.length;

                return (
                  <div key={colecao.collection} className="space-y-2">
                    <div className="flex items-center gap-2 border-b pb-1.5">
                      <Checkbox
                        checked={todosMarcados}
                        onCheckedChange={() =>
                          alternarColecao(ativo as string, colecao.collection, colecao.labels)
                        }
                        aria-label={COLECAO_LABEL[colecao.collection] ?? colecao.collection}
                      />
                      <span className="text-xs font-semibold uppercase tracking-wide">
                        {COLECAO_LABEL[colecao.collection] ?? colecao.collection}
                      </span>
                      <span className="text-[11px] text-muted-foreground">
                        {escolhidos.length}/{colecao.labels.length}
                      </span>
                    </div>

                    <div className="grid gap-1.5 pl-6 sm:grid-cols-2 xl:grid-cols-3">
                      {visiveis.map((rotulo) => (
                        <label
                          key={`${colecao.collection}-${rotulo}`}
                          className={cn(
                            'flex items-center gap-2 rounded px-1 py-0.5 text-xs cursor-pointer hover:bg-muted/60',
                            !escolhidos.includes(rotulo) && 'text-muted-foreground',
                          )}
                        >
                          <Checkbox
                            checked={escolhidos.includes(rotulo)}
                            onCheckedChange={() =>
                              alternarItem(
                                ativo as string,
                                colecao.collection,
                                rotulo,
                                colecao.labels,
                              )
                            }
                          />
                          <span className="truncate" title={rotulo}>
                            {rotulo}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}

            {!carregandoAtivo &&
              ativo &&
              colecoesAtivas.length > 0 &&
              colecoesFiltradas.length === 0 && (
                <p className="text-xs text-muted-foreground">Nenhum item encontrado na busca.</p>
              )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}

export default TemplateModuleSelector;
